import { FormEvent, useState } from "react"
import classNames from "classnames"

import {
  useLazyGetCountriesQuery,
  useLazyGetDeviceLocationsQuery,
  useLazyGetResidentTypesQuery,
  useLazyGetStatesQuery,
  useLazyGetZipCodesQuery,
} from "store/api/dictionaryApi"
import { UITypes } from "types"

import { getAddressLabelsByCountry } from "utils/addressUtils"
import { ButtonNew } from "components/form/ButtonNew"
import CitySuburbSelect from "components/form/CitySuburbSelect"
import { InputNew } from "components/form/InputNew"
import { SearchFnType, SelectDynamic } from "components/form/SelectDynamic"
import { SelectStatic } from "components/form/SelectStatic"

import { CustomerCommonValues } from "."
import { TransferModal } from "./TransferModal"

import styles from "./index.module.scss"

interface DeviceFormProps {
  error: {
    field: string
    message: string
  }
  deviceValues: UITypes.Device
  commonValues: CustomerCommonValues
  onSetDeviceValues: (value: UITypes.Device) => void
  onSetCommonValues: (value: CustomerCommonValues) => void
  onHandleTransferSuccess: () => void
}

const DeviceForm = ({
  deviceValues,
  commonValues,
  error,
  onSetDeviceValues,
  onSetCommonValues,
  onHandleTransferSuccess,
}: DeviceFormProps) => {
  const [showTransferModal, setShowTransferModal] = useState(false)
  const addressLabels = getAddressLabelsByCountry(deviceValues.country?.[0]?.label)

  const onDeviceValuesChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.currentTarget

    onSetDeviceValues({ ...deviceValues, [name]: type === "checkbox" ? checked : value })
  }

  const searchFn: SearchFnType = (option, inputValue) => {
    let search = inputValue
    if (deviceValues.country[0].value === 2) {
      search = search.slice(0, 3)
    }

    return option.label.toLowerCase().includes(search.toLowerCase())
  }

  const handleCountryChange = (country: UITypes.Option[]) => {
    if (deviceValues.country?.[0]?.value === country[0]?.value) {
      return
    }

    onSetDeviceValues({ ...deviceValues, ...{ country, zip: [], state: [], city: [], suburb: [] } })
  }

  const renderCitySuburb = () => {
    const showSuburb = deviceValues.country?.[0]?.value === 3
    const entity = showSuburb ? "suburb" : "city"

    const values = showSuburb ? deviceValues.suburb : deviceValues.city

    return (
      <>
        <span className={styles.modalFieldLabel}>{addressLabels.city}</span>
        <CitySuburbSelect
          showSuburb={showSuburb}
          zip_code={deviceValues.zip?.[0]?.label || ""}
          state_id={deviceValues.state?.[0]?.value != null ? String(deviceValues.state[0].value) : undefined}
          country={deviceValues.country?.[0]?.label || ""}
          values={values || []}
          skipLabel
          onChange={value => onSetDeviceValues({ ...deviceValues, ...{ [entity]: value } })}
          error={error.field === entity ? error.message : ""}
        />
      </>
    )
  }

  const handleTransferSuccess = () => {
    setShowTransferModal(false)
    onHandleTransferSuccess()
  }

  return (
    <div className={styles.modalMainContent}>
      <div className={styles.modalMainContentColumn}>
        <div className={styles.modalFieldWrapper}>
          <span className={classNames(styles.modalFieldLabel)}>Device name</span>
          <InputNew
            placeholder="Enter Device name"
            name="deviceName"
            value={deviceValues.deviceName}
            onChange={onDeviceValuesChange}
            errorMessage={error.field === "deviceName" ? error.message : ""}
          />
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={styles.modalFieldLabel}>Address 1</span>
          <InputNew
            placeholder="Enter Address"
            name="address1"
            value={deviceValues.address1}
            onChange={onDeviceValuesChange}
            errorMessage={error.field === "address1" ? error.message : ""}
          />
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={styles.modalFieldLabel}>Address 2</span>
          <InputNew
            placeholder="Enter Address"
            name="address2"
            value={deviceValues.address2}
            onChange={onDeviceValuesChange}
            errorMessage={error.field === "address2" ? error.message : ""}
          />
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={styles.modalFieldLabel}>Country</span>
          <SelectDynamic
            valueName="id"
            labelName="name"
            name="country"
            useLazyQuery={useLazyGetCountriesQuery}
            placeholder="Enter country"
            values={deviceValues.country}
            isSearchable={false}
            onChange={value => handleCountryChange(value)}
            errorMessage={error.field === "country" ? error.message : ""}
          />
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={styles.modalFieldLabel}>{addressLabels.zip}</span>
          <SelectDynamic
            valueName="id"
            labelName="code"
            name="zip"
            searchFieldName="code"
            useLazyQuery={useLazyGetZipCodesQuery}
            placeholder={`Enter ${addressLabels.zip}`}
            values={deviceValues.zip || []}
            searchFn={searchFn}
            onChange={value => onSetDeviceValues({ ...deviceValues, ...{ zip: value } })}
            queryParams={{ country_id: (deviceValues.country?.[0]?.value || 1) as unknown as number }}
            errorMessage={error.field === "zip" ? error.message : ""}
          />
        </div>
        <div className={styles.modalFieldWrapper}>{renderCitySuburb()}</div>
        <div className={styles.modalFieldWrapper}>
          <span className={styles.modalFieldLabel}>{addressLabels.state}</span>
          <SelectDynamic
            valueName="id"
            labelName="name"
            placeholder={`Enter ${addressLabels.state}`}
            name="state"
            useLazyQuery={useLazyGetStatesQuery}
            values={deviceValues.state || []}
            onChange={value => onSetDeviceValues({ ...deviceValues, ...{ state: value } })}
            errorMessage={error.field === "state" ? error.message : ""}
            queryParams={{ country_id: (deviceValues.country?.[0]?.value || 1) as unknown as number }}
          />
        </div>
      </div>
      <div className={styles.modalMainContentColumn}>
        <div className={styles.modalFieldWrapper}>
          <span className={classNames(styles.modalFieldLabel, styles.wider, styles.serialNumberLabel)}>S/N</span>
          <span className={classNames(styles.modalFieldValue, styles.serialNumberValue)}>
            {deviceValues.serialNumber}
          </span>
          <ButtonNew onClick={() => setShowTransferModal(true)} className={styles.transferButton}>
            Transfer
          </ButtonNew>
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={classNames(styles.modalFieldLabel, styles.wider)}>Work status</span>
          <span
            className={classNames(styles.modalFieldValue, styles.capitalize, {
              [styles.online]: deviceValues.workStatus === "online",
            })}
          >
            {deviceValues.workStatus || "offline"}
          </span>
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={classNames(styles.modalFieldLabel, styles.wider)}>Residence type</span>
          <SelectDynamic
            placeholder="Select residence type"
            useLazyQuery={useLazyGetResidentTypesQuery}
            valueName="id"
            labelName="name"
            values={deviceValues.residentialType || []}
            onChange={value => onSetDeviceValues({ ...deviceValues, ...{ residentialType: value } })}
            errorMessage={error.field === "residentialType" ? error.message : ""}
          />
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={classNames(styles.modalFieldLabel, styles.wider)}>Installation Type</span>
          <SelectStatic
            placeholder="Select Type"
            values={deviceValues.installationType as UITypes.Option[]}
            isSearchable={false}
            options={[
              {
                value: "new",
                label: "New",
              },
              {
                value: "existing",
                label: "Existing",
              },
            ]}
            onChange={value => onSetDeviceValues({ ...deviceValues, ...{ installationType: value } })}
            errorMessage={error.field === "installationType" ? error.message : ""}
          />
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={classNames(styles.modalFieldLabel, styles.wider)}>Device location</span>
          <SelectDynamic
            placeholder="Select device location"
            useLazyQuery={useLazyGetDeviceLocationsQuery}
            valueName="id"
            labelName="name"
            queryParams={{ rt: deviceValues.residentialType?.[0]?.label?.toLowerCase() || "" }}
            values={deviceValues.deviceLocation || []}
            onChange={value => onSetDeviceValues({ ...deviceValues, ...{ deviceLocation: value } })}
            errorMessage={error.field === "deviceLocation" ? error.message : ""}
          />
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={classNames(styles.modalFieldLabel, styles.wider)}>Membership</span>
          <SelectStatic
            placeholder="Select Membership"
            values={commonValues?.membership as UITypes.Option[]}
            options={[
              {
                value: "yes",
                label: "Yes",
              },
              {
                value: "no",
                label: "No",
              },
            ]}
            onChange={value => onSetCommonValues({ ...commonValues, ...{ membership: value } })}
            errorMessage={error.field === "membership" ? error.message : ""}
          />
        </div>
        <div className={styles.modalFieldWrapper}>
          <span className={classNames(styles.modalFieldLabel, styles.wider)}>HVAC system age</span>
          <SelectStatic
            placeholder="Select system age"
            values={deviceValues.systemAge as UITypes.Option[]}
            options={Array.from({ length: 30 }, (_, i) => ({
              value: (i + 1).toString(),
              label: `${i + 1} ${i + 1 === 1 ? "Year" : "Years"}`,
            }))}
            onChange={value => onSetDeviceValues({ ...deviceValues, ...{ systemAge: value } })}
            errorMessage={error.field === "systemAge" ? error.message : ""}
          />
        </div>
      </div>
      {showTransferModal && (
        <TransferModal
          serialNumber={deviceValues.serialNumber}
          onCancel={() => setShowTransferModal(false)}
          onSuccess={() => {
            handleTransferSuccess()
          }}
        />
      )}
    </div>
  )
}

export default DeviceForm
